import styled, { css } from "styled-components";
import { FC } from "react";
import { DateTime } from "luxon";

import Text from "@/components/Text";
import { ImageDistributionStatus } from "@/utils/enums";
import { FBImageDistributionResource } from "@/types/resources";

const InfoCardBox = styled.div`
  margin: 16px 0;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  padding: 24px;
  position: relative;
  &:after {
    content: "";
    position: absolute;
    left: 50%;
    width: 1px;
    background-color: ${({ theme }) => theme.colors.lightGray};
    top: 24px;
    bottom: 24px;
  }
`;

const InfoCardGroupsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 48px;
  grid-row-gap: 24px;
`;

const InfoCardItem = styled.div`
  ${Text} {
    &:first-child {
      margin-bottom: 2px;
    }
  }
  &:nth-child(1),
  &:nth-child(2) {
    min-height: 60px;
  }
`;

const StatusDot = styled.span<{ $status: ImageDistributionStatus }>`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  ${({ theme, $status }) =>
    $status === ImageDistributionStatus.POSTED &&
    css`
      background: ${theme.colors.success};
    `}
  ${({ theme, $status }) =>
    $status === ImageDistributionStatus.SCHEDULED &&
    css`
      background: ${theme.colors.info};
    `}
    ${({ theme, $status }) =>
    $status === ImageDistributionStatus.DRAFT &&
    css`
      background: ${theme.colors.darkGray};
    `}
`;

const StatusText = styled(Text)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface Props {
  post: FBImageDistributionResource;
}

const InfoCard: FC<Props> = ({ post }) => {
  const now = DateTime.now();

  const getStatus = () => {
    if (post.status === ImageDistributionStatus.DRAFT) {
      return ImageDistributionStatus.DRAFT;
    }
    if (post.status === ImageDistributionStatus.POSTED) {
      if (DateTime.fromISO(post.postDate!) > now) {
        return ImageDistributionStatus.SCHEDULED;
      }
      return ImageDistributionStatus.POSTED;
    }
    return ImageDistributionStatus.DRAFT;
  };

  const status = getStatus();

  const renderStatus = () => {
    switch (status) {
      case ImageDistributionStatus.DRAFT:
        return "Draft";
      case ImageDistributionStatus.POSTED:
        return "Posted";
      case ImageDistributionStatus.SCHEDULED:
        return "Scheduled";
    }
  };

  return (
    <InfoCardBox>
      <InfoCardGroupsWrapper>
        <InfoCardItem>
          <Text $type="smallBold" $color="gray">
            Post Name
          </Text>
          <Text $type="caption">{post.name}</Text>
        </InfoCardItem>
        <InfoCardItem>
          <Text $type="smallBold" $color="gray">
            Created At
          </Text>
          <Text $type="caption">
            {DateTime.fromISO(post.createdAt).toFormat("yyyy-LL-dd · HH:mm")}
          </Text>
        </InfoCardItem>
        <InfoCardItem>
          <Text $type="smallBold" $color="gray">
            Status
          </Text>
          <StatusText $type="caption">
            <StatusDot $status={status} />
            {renderStatus()}
          </StatusText>
        </InfoCardItem>
        <InfoCardItem>
          <Text $type="smallBold" $color="gray">
            Owner
          </Text>
          <Text $type="caption">{post.owner}</Text>
        </InfoCardItem>
      </InfoCardGroupsWrapper>
    </InfoCardBox>
  );
};

export default InfoCard;
